{
  "version": "0.1.38",
  "packageName": "@classic-homes/notifications",
  "description": "Notification services and Svelte bindings for Classic Theme apps",
  "subpackages": {
    "core": {
      "importPath": "@classic-homes/notifications/core",
      "description": "Framework-agnostic notification core. Use this for direct API access or custom integrations.",
      "modules": {
        "api": {
          "description": "Notification API client methods for all notification endpoints",
          "functions": [
            {
              "name": "notificationsApi.list",
              "signature": "(params?: NotificationListParams, customFetch?: typeof fetch) => Promise<NotificationsListResponse>",
              "description": "List notifications with optional filters and pagination",
              "example": "import { notificationsApi } from '@classic-homes/notifications/core';\n\nconst response = await notificationsApi.list({\n  page: 1,\n  limit: 20,\n  unreadOnly: true,\n  type: 'security'\n});\n\nconsole.log(response.notifications);\nconsole.log(response.unreadCount);\nconsole.log(response.pagination);"
            },
            {
              "name": "notificationsApi.get",
              "signature": "(id: string, customFetch?: typeof fetch) => Promise<Notification>",
              "description": "Get a single notification by ID"
            },
            {
              "name": "notificationsApi.markRead",
              "signature": "(id: string) => Promise<void>",
              "description": "Mark a notification as read"
            },
            {
              "name": "notificationsApi.markManyRead",
              "signature": "(ids: string[]) => Promise<void>",
              "description": "Mark multiple notifications as read"
            },
            {
              "name": "notificationsApi.markAllRead",
              "signature": "() => Promise<void>",
              "description": "Mark all notifications as read"
            },
            {
              "name": "notificationsApi.dismiss",
              "signature": "(id: string) => Promise<void>",
              "description": "Dismiss (delete) a notification"
            },
            {
              "name": "notificationsApi.bulkDelete",
              "signature": "(ids: string[]) => Promise<void>",
              "description": "Delete multiple notifications in bulk"
            },
            {
              "name": "notificationsApi.deleteAll",
              "signature": "() => Promise<void>",
              "description": "Delete all notifications"
            },
            {
              "name": "notificationsApi.getChannels",
              "signature": "(customFetch?: typeof fetch) => Promise<ChannelsResponse>",
              "description": "Get available notification channels (email, sms, push, in_app)"
            },
            {
              "name": "notificationsApi.getPreferences",
              "signature": "(params?: { channelId?: string; notificationType?: NotificationType }, customFetch?: typeof fetch) => Promise<PreferencesResponse>",
              "description": "Get notification preferences with optional filters"
            },
            {
              "name": "notificationsApi.updatePreference",
              "signature": "(preference: UpdatePreferenceData) => Promise<UpdatePreferenceResponse>",
              "description": "Update a notification preference"
            },
            {
              "name": "notificationsApi.batchUpdatePreferences",
              "signature": "(preferences: BatchPreferenceUpdate[]) => Promise<BulkPreferenceResponse>",
              "description": "Batch update multiple notification preferences"
            },
            {
              "name": "notificationsApi.deletePreference",
              "signature": "(subscriptionId: string) => Promise<DeletePreferenceResponse>",
              "description": "Delete a notification preference"
            },
            {
              "name": "notificationsApi.generateTestNotifications",
              "signature": "(params?: TestNotificationParams) => Promise<TestNotificationResponse>",
              "description": "Generate test notifications (dev/admin only)"
            }
          ]
        },
        "service": {
          "description": "Notification service with cleaner interface for business logic",
          "functions": [
            {
              "name": "NotificationService",
              "signature": "class",
              "description": "Service class that wraps notification API and provides a clean interface",
              "example": "import { notificationService } from '@classic-homes/notifications/core';\n\n// Use the singleton instance\nconst response = await notificationService.list({ page: 1, limit: 20 });\nawait notificationService.markRead('notification-id');\nawait notificationService.markAllRead();"
            },
            {
              "name": "notificationService",
              "signature": "NotificationService",
              "description": "Singleton instance of NotificationService"
            }
          ]
        },
        "utils": {
          "description": "Utility functions for notification display and formatting",
          "functions": [
            {
              "name": "getNotificationIconName",
              "signature": "(type: NotificationType) => string",
              "description": "Get the icon name for a notification type (security→shield, system→settings, feature→zap, update→refresh, alert→alert-circle, info→info)"
            },
            {
              "name": "getNotificationIconColor",
              "signature": "(type: NotificationType) => string",
              "description": "Get the Tailwind color class for a notification type (security→text-red-500, system→text-blue-500, feature→text-purple-500, update→text-green-500, alert→text-yellow-500, info→text-gray-500)"
            },
            {
              "name": "getPriorityBadgeVariant",
              "signature": "(priority: NotificationPriority) => BadgeVariant",
              "description": "Get the badge variant for a priority (urgent→destructive, high→warning, normal→secondary, low→outline)"
            },
            {
              "name": "getTypeBadgeVariant",
              "signature": "(type: NotificationType) => BadgeVariant",
              "description": "Get the badge variant for a notification type (security→destructive, system→info, feature→default, update→success, alert→warning, info→secondary)"
            },
            {
              "name": "getPriorityLabel",
              "signature": "(priority: NotificationPriority) => string",
              "description": "Get the display label for a priority (urgent→Urgent, high→High, normal→Normal, low→Low)"
            },
            {
              "name": "getTypeLabel",
              "signature": "(type: NotificationType) => string",
              "description": "Get the display label for a notification type (security→Security, system→System, etc.)"
            },
            {
              "name": "formatRelativeTime",
              "signature": "(timestamp: string) => string",
              "description": "Format timestamp as relative time ('just now', '5m ago', '2h ago', 'yesterday', '3d ago', '2w ago', '1mo ago')"
            },
            {
              "name": "formatFullTimestamp",
              "signature": "(timestamp: string) => string",
              "description": "Format timestamp as full readable date/time ('Mon, Jan 22, 2025 2:30 PM')"
            },
            {
              "name": "formatNotificationTime",
              "signature": "(timestamp: string) => string",
              "description": "Smart formatting - relative for recent (<7 days), date for older ('Jan 15')"
            },
            {
              "name": "getPrioritySortValue",
              "signature": "(priority: NotificationPriority) => number",
              "description": "Get sort value for priority (urgent=0, high=1, normal=2, low=3)"
            }
          ]
        }
      },
      "types": [
        {
          "name": "NotificationType",
          "description": "Type of notification",
          "type": "'security' | 'system' | 'feature' | 'update' | 'alert' | 'info'"
        },
        {
          "name": "NotificationPriority",
          "description": "Priority level of notification",
          "type": "'urgent' | 'high' | 'normal' | 'low'"
        },
        {
          "name": "Notification",
          "description": "Notification object",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Notification ID"
            },
            {
              "name": "userId",
              "type": "string",
              "required": true,
              "description": "User ID"
            },
            {
              "name": "type",
              "type": "NotificationType",
              "required": true,
              "description": "Notification type"
            },
            {
              "name": "category",
              "type": "string",
              "required": true,
              "description": "Notification category"
            },
            {
              "name": "title",
              "type": "string",
              "required": true,
              "description": "Notification title"
            },
            {
              "name": "message",
              "type": "string",
              "required": true,
              "description": "Notification message"
            },
            {
              "name": "icon",
              "type": "string",
              "required": false,
              "description": "Optional icon name"
            },
            {
              "name": "color",
              "type": "string",
              "required": false,
              "description": "Optional color"
            },
            {
              "name": "priority",
              "type": "NotificationPriority",
              "required": true,
              "description": "Priority level"
            },
            {
              "name": "read",
              "type": "boolean",
              "required": true,
              "description": "Whether notification has been read"
            },
            {
              "name": "dismissed",
              "type": "boolean",
              "required": true,
              "description": "Whether notification has been dismissed"
            },
            {
              "name": "actionUrl",
              "type": "string",
              "required": false,
              "description": "Optional action URL"
            },
            {
              "name": "actionLabel",
              "type": "string",
              "required": false,
              "description": "Optional action button label"
            },
            {
              "name": "metadata",
              "type": "Record<string, unknown>",
              "required": false,
              "description": "Additional metadata"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Creation timestamp"
            },
            {
              "name": "readAt",
              "type": "string",
              "required": false,
              "description": "When notification was read"
            },
            {
              "name": "dismissedAt",
              "type": "string",
              "required": false,
              "description": "When notification was dismissed"
            }
          ]
        },
        {
          "name": "NotificationsListResponse",
          "description": "Response from listing notifications",
          "properties": [
            {
              "name": "notifications",
              "type": "Notification[]",
              "required": true,
              "description": "Array of notifications"
            },
            {
              "name": "unreadCount",
              "type": "number",
              "required": true,
              "description": "Total unread count"
            },
            {
              "name": "pagination",
              "type": "{ page: number; limit: number; total: number; totalPages: number }",
              "required": true,
              "description": "Pagination info"
            }
          ]
        },
        {
          "name": "NotificationListParams",
          "description": "Parameters for listing notifications",
          "properties": [
            {
              "name": "page",
              "type": "number",
              "required": false,
              "description": "Page number (default: 1)"
            },
            {
              "name": "limit",
              "type": "number",
              "required": false,
              "description": "Items per page (default: 20)"
            },
            {
              "name": "unreadOnly",
              "type": "boolean",
              "required": false,
              "description": "Only return unread notifications"
            },
            {
              "name": "readOnly",
              "type": "boolean",
              "required": false,
              "description": "Only return read notifications"
            },
            {
              "name": "type",
              "type": "NotificationType",
              "required": false,
              "description": "Filter by notification type"
            }
          ]
        },
        {
          "name": "ChannelType",
          "description": "Notification channel type",
          "type": "'email' | 'sms' | 'push' | 'in_app'"
        },
        {
          "name": "NotificationChannel",
          "description": "Notification channel configuration",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Channel ID"
            },
            {
              "name": "name",
              "type": "string",
              "required": true,
              "description": "Channel name"
            },
            {
              "name": "displayName",
              "type": "string",
              "required": true,
              "description": "Display name"
            },
            {
              "name": "description",
              "type": "string",
              "required": false,
              "description": "Channel description"
            },
            {
              "name": "enabled",
              "type": "boolean",
              "required": true,
              "description": "Whether channel is enabled"
            },
            {
              "name": "priority",
              "type": "number",
              "required": true,
              "description": "Channel priority"
            }
          ]
        },
        {
          "name": "NotificationFrequency",
          "description": "Notification delivery frequency",
          "type": "'immediate' | 'digest_hourly' | 'digest_daily' | 'digest_weekly'"
        },
        {
          "name": "NotificationPreference",
          "description": "User notification preference",
          "properties": [
            {
              "name": "id",
              "type": "string",
              "required": true,
              "description": "Preference ID"
            },
            {
              "name": "userId",
              "type": "string",
              "required": true,
              "description": "User ID"
            },
            {
              "name": "channelId",
              "type": "string",
              "required": true,
              "description": "Channel ID"
            },
            {
              "name": "notificationType",
              "type": "NotificationType",
              "required": true,
              "description": "Notification type"
            },
            {
              "name": "notificationCategory",
              "type": "string",
              "required": false,
              "description": "Notification category"
            },
            {
              "name": "enabled",
              "type": "boolean",
              "required": true,
              "description": "Whether preference is enabled"
            },
            {
              "name": "frequency",
              "type": "NotificationFrequency",
              "required": true,
              "description": "Delivery frequency"
            },
            {
              "name": "quietHoursStart",
              "type": "string",
              "required": false,
              "description": "Quiet hours start time"
            },
            {
              "name": "quietHoursEnd",
              "type": "string",
              "required": false,
              "description": "Quiet hours end time"
            },
            {
              "name": "quietHoursTimezone",
              "type": "string",
              "required": false,
              "description": "Quiet hours timezone"
            },
            {
              "name": "minPriority",
              "type": "NotificationPriority",
              "required": true,
              "description": "Minimum priority to receive"
            },
            {
              "name": "tags",
              "type": "string[]",
              "required": false,
              "description": "Filter tags"
            },
            {
              "name": "createdAt",
              "type": "string",
              "required": true,
              "description": "Creation timestamp"
            },
            {
              "name": "updatedAt",
              "type": "string",
              "required": false,
              "description": "Last update timestamp"
            }
          ]
        },
        {
          "name": "UpdatePreferenceData",
          "description": "Data for updating a preference",
          "properties": [
            {
              "name": "channelId",
              "type": "string",
              "required": true,
              "description": "Channel ID"
            },
            {
              "name": "notificationType",
              "type": "NotificationType",
              "required": true,
              "description": "Notification type"
            },
            {
              "name": "notificationCategory",
              "type": "string",
              "required": false,
              "description": "Notification category"
            },
            {
              "name": "enabled",
              "type": "boolean",
              "required": true,
              "description": "Whether to enable"
            },
            {
              "name": "frequency",
              "type": "NotificationFrequency",
              "required": false,
              "description": "Delivery frequency"
            },
            {
              "name": "quietHoursStart",
              "type": "string",
              "required": false,
              "description": "Quiet hours start"
            },
            {
              "name": "quietHoursEnd",
              "type": "string",
              "required": false,
              "description": "Quiet hours end"
            },
            {
              "name": "quietHoursTimezone",
              "type": "string",
              "required": false,
              "description": "Timezone"
            },
            {
              "name": "minPriority",
              "type": "NotificationPriority",
              "required": false,
              "description": "Minimum priority"
            },
            {
              "name": "tags",
              "type": "string[]",
              "required": false,
              "description": "Filter tags"
            }
          ]
        },
        {
          "name": "BatchPreferenceUpdate",
          "description": "Batch preference update request",
          "properties": [
            {
              "name": "eventType",
              "type": "string",
              "required": true,
              "description": "Event type"
            },
            {
              "name": "channels",
              "type": "ChannelType[]",
              "required": false,
              "description": "Channels to update"
            },
            {
              "name": "enabled",
              "type": "boolean",
              "required": false,
              "description": "Enable/disable"
            },
            {
              "name": "minPriority",
              "type": "NotificationPriority",
              "required": false,
              "description": "Minimum priority"
            }
          ]
        },
        {
          "name": "NotificationFilter",
          "description": "Filter type for notification lists",
          "type": "'all' | 'unread' | 'read'"
        },
        {
          "name": "NotificationCounts",
          "description": "Counts for filter tabs",
          "properties": [
            {
              "name": "all",
              "type": "number",
              "required": true,
              "description": "Total count"
            },
            {
              "name": "unread",
              "type": "number",
              "required": true,
              "description": "Unread count"
            },
            {
              "name": "read",
              "type": "number",
              "required": true,
              "description": "Read count"
            }
          ]
        },
        {
          "name": "BadgeVariant",
          "description": "Badge component variant",
          "type": "'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'"
        }
      ]
    },
    "svelte": {
      "importPath": "@classic-homes/notifications/svelte",
      "description": "Svelte-specific bindings for notifications. Includes reactive stores for notification state.",
      "modules": {
        "stores": {
          "description": "Reactive Svelte stores for notification state",
          "functions": [
            {
              "name": "notificationStore",
              "signature": "{ subscribe, fetch, refresh, loadMore, markRead, markManyRead, markAllRead, dismiss, deleteAll, clearError, reset }",
              "description": "Main notification store with methods for managing notifications",
              "example": "import { notificationStore } from '@classic-homes/notifications/svelte';\n\n// Fetch notifications\nawait notificationStore.fetch({ page: 1, limit: 20 });\n\n// Mark as read\nawait notificationStore.markRead('notification-id');\nawait notificationStore.markAllRead();\n\n// Delete\nawait notificationStore.dismiss('notification-id');\n\n// Subscribe to state changes\nnotificationStore.subscribe(state => {\n  console.log('Notifications:', state.notifications);\n  console.log('Unread:', state.unreadCount);\n});"
            },
            {
              "name": "notifications",
              "signature": "{ subscribe: (fn: (value: Notification[]) => void) => () => void }",
              "description": "Derived store for notifications array",
              "example": "import { notifications } from '@classic-homes/notifications/svelte';\n\n// In Svelte component\n{#each $notifications as notification}\n  <p>{notification.title}</p>\n{/each}"
            },
            {
              "name": "unreadCount",
              "signature": "{ subscribe: (fn: (value: number) => void) => () => void }",
              "description": "Derived store for unread notification count",
              "example": "import { unreadCount } from '@classic-homes/notifications/svelte';\n\n// In Svelte component\n{#if $unreadCount > 0}\n  <Badge>{$unreadCount}</Badge>\n{/if}"
            },
            {
              "name": "isLoadingNotifications",
              "signature": "{ subscribe: (fn: (value: boolean) => void) => () => void }",
              "description": "Derived store for loading state",
              "example": "import { isLoadingNotifications } from '@classic-homes/notifications/svelte';\n\n// In Svelte component\n{#if $isLoadingNotifications}\n  <Spinner />\n{/if}"
            }
          ]
        }
      },
      "storeState": {
        "name": "NotificationState",
        "description": "State managed by the notification store",
        "properties": [
          {
            "name": "notifications",
            "type": "Notification[]",
            "required": true,
            "description": "Array of notifications"
          },
          {
            "name": "unreadCount",
            "type": "number",
            "required": true,
            "description": "Number of unread notifications"
          },
          {
            "name": "isLoading",
            "type": "boolean",
            "required": true,
            "description": "Whether notifications are loading"
          },
          {
            "name": "error",
            "type": "string | null",
            "required": true,
            "description": "Error message if any"
          },
          {
            "name": "pagination",
            "type": "{ page: number; limit: number; total: number; totalPages: number }",
            "required": true,
            "description": "Pagination state"
          }
        ]
      },
      "storeMethods": [
        {
          "name": "fetch",
          "signature": "(params?: NotificationListParams) => Promise<void>",
          "description": "Fetch notifications from the API"
        },
        {
          "name": "refresh",
          "signature": "() => Promise<void>",
          "description": "Refresh notifications with current pagination params"
        },
        {
          "name": "loadMore",
          "signature": "() => Promise<void>",
          "description": "Load the next page of notifications"
        },
        {
          "name": "markRead",
          "signature": "(id: string) => Promise<void>",
          "description": "Mark a single notification as read"
        },
        {
          "name": "markManyRead",
          "signature": "(ids: string[]) => Promise<void>",
          "description": "Mark multiple notifications as read"
        },
        {
          "name": "markAllRead",
          "signature": "() => Promise<void>",
          "description": "Mark all notifications as read"
        },
        {
          "name": "dismiss",
          "signature": "(id: string) => Promise<void>",
          "description": "Delete a single notification"
        },
        {
          "name": "deleteAll",
          "signature": "() => Promise<void>",
          "description": "Delete all notifications"
        },
        {
          "name": "clearError",
          "signature": "() => void",
          "description": "Clear the error state"
        },
        {
          "name": "reset",
          "signature": "() => void",
          "description": "Reset store to initial state"
        }
      ]
    }
  }
}